import { useEffect, useState, useMemo } from "react";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";
import { calcGainMassiqueImpregPli } from "../utils/humiditeCalc";

function fmtHumidite(v, digits = 4) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(digits)} %`;
}

function stackHumiditeMap(session) {
  return {
    ...(session?.HumiditeParStack || {}),
    ...(session?.stats?.humidite_par_stack || {}),
  };
}

function resolveStackHumidite(session, stackId) {
  const map = stackHumiditeMap(session);
  if (stackId && map[stackId] != null) return Number(map[stackId]);
  const stacks = session?.StackPNs || [];
  if (stacks.length === 1 && (!stackId || stacks[0] === stackId)) {
    const s =
      session?.HumiditeApresImpregnation ??
      session?.stats?.humidite_apres_impreg_stack;
    if (s != null) return Number(s);
  }
  return null;
}

function groupImpregPlisByStack(session) {
  const stacks = session?.StackPNs || [];
  const rows = session?.Rows || [];
  if (!rows.length) return [];

  const hasStackId = rows.some((r) => String(r.StackId || "").trim());

  if (!hasStackId && stacks.length <= 1) {
    const sid = stacks[0] || "Stack";
    return [
      {
        stackId: sid,
        rows,
        hStack: resolveStackHumidite(session, sid),
      },
    ];
  }

  const byStack = {};
  stacks.forEach((s) => {
    byStack[s] = {
      stackId: s,
      rows: [],
      hStack: resolveStackHumidite(session, s),
    };
  });

  rows.forEach((r) => {
    const sid = String(r.StackId || "").trim() || stacks[0] || "—";
    if (!byStack[sid]) {
      byStack[sid] = {
        stackId: sid,
        rows: [],
        hStack: resolveStackHumidite(session, sid),
      };
    }
    byStack[sid].rows.push(r);
  });

  return Object.values(byStack).filter((g) => g.rows.length > 0);
}

function HumiditePlisTables({
  session,
  editMode = false,
  editMasses = {},
  setEditMasses = () => {},
  showMasses = true,
}) {
  const groups = groupImpregPlisByStack(session);
  if (!groups.length) {
    return (
      <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
        Aucun pli enregistré.
      </div>
    );
  }

  return (
    <>
      {groups.map((group) => (
        <div key={group.stackId} style={{ marginBottom: 20 }}>
          <div style={S.humStackBar}>
            <span style={S.stackBadgeLg}>{group.stackId}</span>
            <span style={S.humStackLabel}>Plis du stack · humidités après imprégnation</span>
          </div>

          <div style={{ ...S.tableWrap, marginBottom: 8 }} className="woodoo-scroll">
            <table style={S.table}>
              <thead>
                <tr>
                  {[
                    "Pos.",
                    "Pli PN",
                    ...(showMasses ? ["M. cond. (g)", "M. imprég. (g)", "Gain (%)"] : []),
                    "H stack (%)",
                    "H pli (%)",
                  ].map((h) => (
                    <th key={h} className="woodoo-th" style={S.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.rows.map((pli, i) => (
                  <tr
                    key={`${group.stackId}-${pli.Position}-${i}`}
                    className="woodoo-tr"
                    style={i % 2 === 0 ? S.trEven : S.trOdd}
                  >
                    <td style={S.tdIndex}>{pli.Position}</td>
                    <td style={{ ...S.td, ...S.tdStack, textAlign: "left" }}>
                      {pli.PlyPN || "—"}
                    </td>
                    {showMasses && (
                      <>
                        <td style={S.td}>
                          {pli.MasseConditionnement != null
                            ? pli.MasseConditionnement.toFixed(2)
                            : "—"}
                        </td>
                        <td style={S.td}>
                          {editMode ? (
                            <input
                              className="woodoo-input"
                              type="number"
                              min="0"
                              step="0.01"
                              value={editMasses[pli.Position] ?? ""}
                              onChange={(e) =>
                                setEditMasses((prev) => ({
                                  ...prev,
                                  [pli.Position]: e.target.value,
                                }))
                              }
                              style={{
                                width: 110,
                                padding: "5px 8px",
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 700,
                                textAlign: "center",
                                borderColor:
                                  editMasses[pli.Position] !=
                                  pli.MasseApresTraitement
                                    ? "#E6FF3E"
                                    : "#1e3a50",
                              }}
                            />
                          ) : pli.MasseApresTraitement != null ? (
                            pli.MasseApresTraitement.toFixed(2)
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={S.td}>
                          {editMode &&
                          editMasses[pli.Position] != null
                            ? (() => {
                                const mi = parseFloat(editMasses[pli.Position]);
                                if (Number.isNaN(mi)) return "—";
                                const gain = calcGainMassiqueImpregPli(
                                  mi,
                                  pli.MasseAnhydre
                                );
                                if (gain == null) return "—";
                                return (
                                  <span
                                    style={{
                                      color: gain > 0 ? "#4ade80" : "#f87171",
                                      fontWeight: 800,
                                    }}
                                  >
                                    {gain.toFixed(2)} %
                                  </span>
                                );
                              })()
                            : pli.GainMassique != null
                              ? (
                                  <span
                                    style={{
                                      color:
                                        pli.GainMassique > 0
                                          ? "#4ade80"
                                          : "#f87171",
                                      fontWeight: 800,
                                    }}
                                  >
                                    {pli.GainMassique.toFixed(2)} %
                                  </span>
                                )
                              : "—"}
                        </td>
                      </>
                    )}
                    {i === 0 && (
                      <td
                        rowSpan={group.rows.length}
                        style={{
                          ...S.td,
                          color: "#72b7b2",
                          fontWeight: 900,
                          fontSize: 15,
                          verticalAlign: "middle",
                          borderRight: "1px solid #1e3a50",
                          background: "rgba(114,183,178,0.06)",
                        }}
                      >
                        {fmtHumidite(group.hStack)}
                      </td>
                    )}
                    <td style={{ ...S.td, color: "#a7e0dc", fontWeight: 800 }}>
                      {fmtHumidite(pli.HumiditeApresImpregnation)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

export default function ConsultationImpregnation() {
  const { t, ui } = useI18n();

  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState("");
  const [details, setDetails]     = useState(null);   // modal détail imprégnation
  const [plisPopup, setPlisPopup] = useState(null);   // modal plis d'une entrée
  const [editMasses, setEditMasses]   = useState({});  // { Position: newMasse }
const [saving, setSaving]           = useState(false);
const [editMode, setEditMode]       = useState(false);

  const [filters, setFilters] = useState({
    search: "", melange_pn: "", stack_id: "", date: "",
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const openEdit = (r) => {
  // Pré-remplit avec les masses actuelles
  const init = {};
  (r.Rows || []).forEach(pli => {
    init[pli.Position] = pli.MasseApresTraitement ?? "";
  });
  setEditMasses(init);
  setEditMode(true);
  setDetails(r);
};

const handleSaveMasses = async () => {
  if (!details) return;
  setSaving(true);
  try {
    const rows = Object.entries(editMasses)
      .map(([pos, masse]) => ({
        Position:             parseInt(pos),
        MasseApresTraitement: parseFloat(masse),
      }))
      .filter(r => !isNaN(r.MasseApresTraitement));

    const resp = await apiFetch("/api/impregnation/update", {
      method:  "PUT",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ time: details.time, rows }),
    });
    const out = await resp.json();

    if (out.status !== "success") {
      alert(out.message || "Erreur lors de la mise à jour");
      return;
    }

    // Met à jour le state local
    const patch = {
      Rows: out.rows,
      stats: out.stats,
      HumiditeApresImpregnation: out.humiditeApresImpregnation,
      HumiditeParStack: out.humiditeParStack,
    };
    setRows((prev) =>
      prev.map((r) => (r.time === details.time ? { ...r, ...patch } : r))
    );
    setDetails((prev) => ({ ...prev, ...patch }));
    setEditMode(false);
    setEditMasses({});

  } catch {
    alert("Erreur réseau");
  } finally {
    setSaving(false);
  }
};

  // ── Chargement ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/api/impregnation?per_page=500");
        const j = await r.json();
        setRows(j.data || []);
      } catch {
        setErr("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Filtres + tri ─────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let result = [...rows];

    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(r =>
        (r.StackPNs || []).join(" ").toLowerCase().includes(s) ||
        (r.MelangePN || "").toLowerCase().includes(s) ||
        (r.Operateur || "").toLowerCase().includes(s)
      );
    }
    if (filters.melange_pn) {
      result = result.filter(r =>
        (r.MelangePN || "").toLowerCase().includes(filters.melange_pn.toLowerCase())
      );
    }
    if (filters.stack_id) {
      result = result.filter(r =>
        (r.StackPNs || []).some(s => s.toLowerCase().includes(filters.stack_id.toLowerCase()))
      );
    }
    if (filters.date) {
      result = result.filter(r => (r.time || "").startsWith(filters.date));
    }

    if (sortConfig.key) {
      const { key, direction } = sortConfig;
      result = [...result].sort((a, b) => {
        const aV = a[key] ?? ""; const bV = b[key] ?? "";
        const isNum = v => v !== "" && !isNaN(v);
        if (isNum(aV) && isNum(bV))
          return direction === "asc" ? Number(aV) - Number(bV) : Number(bV) - Number(aV);
        return direction === "asc"
          ? String(aV).localeCompare(String(bV))
          : String(bV).localeCompare(String(aV));
      });
    }
    return result;
  }, [rows, filters, sortConfig]);

  const totalPages   = Math.ceil(filteredRows.length / PAGE_SIZE);
  const paginatedRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <span style={S.sortIcon}>⇅</span>;
    return <span style={{ ...S.sortIcon, color: "#E6FF3E" }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>;
  };

  const setFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({ search: "", melange_pn: "", stack_id: "", date: "" });
    setSortConfig({ key: null, direction: "asc" });
    setCurrentPage(1);
  };

  // ── Export CSV ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (!filteredRows.length) return;
    const headers = ["Date", "Opérateur", "Stack(s)", "Résine", "Température (°C)",
      "Durée (min)", "Nb Plis", "Gain Moyen (%)", "Gain Min (%)", "Gain Max (%)"];
    const csvRows = filteredRows.map(r => [
      r.time || "", r.Operateur || "",
      (r.StackPNs || []).join(";"), r.MelangePN || "",
      r.Temperature ?? "", r.DureeTraitement ?? "",
      r.stats?.nb_plis ?? "", r.stats?.gain_moyen ?? "",
      r.stats?.gain_min ?? "", r.stats?.gain_max ?? "",
    ]);
    const csv = [headers, ...csvRows]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `impregnation_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ── Stats header ──────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total: rows.length,
    avgGain: rows.length
      ? (rows.reduce((acc, r) => acc + (r.stats?.gain_moyen ?? 0), 0) / rows.length).toFixed(2)
      : "—",
    totalPlis: rows.reduce((acc, r) => acc + (r.stats?.nb_plis ?? 0), 0),
  }), [rows]);

  return (
    <>
      <style>{globalCSS}</style>
      <div style={S.page}>
        <MenuBar active="production" />
        <div style={S.wrap}>

          {/* ── Header ── */}
          <div style={S.header}>
            <div>
              <div style={S.headerTitle}>Imprégnation</div>
              <div style={S.headerSub}>Historique des traitements & mesures par pli</div>
            </div>
            <div style={S.statsRow}>
              <div style={S.statCard}>
                <div style={S.statNum}>{stats.total}</div>
                <div style={S.statLabel}>Sessions</div>
              </div>
              <div style={{ ...S.statCard, borderColor: "#E6FF3E" }}>
                <div style={{ ...S.statNum, color: "#E6FF3E" }}>{stats.totalPlis}</div>
                <div style={S.statLabel}>Plis traités</div>
              </div>
              <div style={{ ...S.statCard, borderColor: "#60a5fa" }}>
                <div style={{ ...S.statNum, color: "#60a5fa" }}>{stats.avgGain} %</div>
                <div style={S.statLabel}>Gain moyen</div>
              </div>
            </div>
          </div>

          {/* ── Panel ── */}
          <div style={S.panel}>

            {/* ── Filtres ── */}
            <div style={S.filterBar}>
              {[
                { key: "search",     placeholder: "Recherche globale",  icon: "🔍" },
                { key: "stack_id",   placeholder: "ID Stack",           icon: "🔖" },
                { key: "melange_pn", placeholder: "Résine (MelangePN)", icon: "🧪" },
                { key: "date",       placeholder: "Date (YYYY-MM-DD)",  icon: "📅" },
              ].map(({ key, placeholder, icon }) => (
                <div key={key} style={S.filterWrap}>
                  <span style={S.filterIcon}>{icon}</span>
                  <input
                    className="woodoo-input"
                    type="text"
                    placeholder={placeholder}
                    value={filters[key]}
                    onChange={e => setFilter(key, e.target.value)}
                    style={S.filterInput}
                  />
                </div>
              ))}
              <button className="woodoo-btn-lime" style={S.exportBtn} onClick={exportCSV}>
                📥 Export CSV
              </button>
              <button className="woodoo-btn-reset" style={S.resetBtn} onClick={resetFilters}>
                ✕ Reset
              </button>
            </div>

            <div style={S.resultCount}>
              <span style={S.resultDot} />
              {filteredRows.length} résultat(s) sur {rows.length}
            </div>

            {loading && <div style={S.loading}><span className="woodoo-spin">⟳</span> {ui("Chargement…")}</div>}
            {err     && <div style={S.errMsg}>{err}</div>}

            {/* ── Table ── */}
            {!loading && (
              <div style={S.tableWrap} className="woodoo-scroll">
                <table style={S.table}>
                  <thead>
                    <tr>
                      {[
                        ["time",            "Date"],
                        ["Operateur",       "Opérateur"],
                        ["MelangePN",       "Résine"],
                        ["Temperature",     "Temp. (°C)"],
                        ["DureeTraitement", "Durée (min)"],
                        ["TotalPlis",       "Nb Plis"],
                      ].map(([key, label]) => (
                        <th key={key} className="woodoo-th" style={S.th} onClick={() => handleSort(key)}>
                          {label} {getSortIcon(key)}
                        </th>
                      ))}
                      <th style={S.th}>Stacks</th>
                      <th style={S.th}>Gain moy.</th>
                      <th style={{ ...S.th, cursor: "default" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((r, i) => (
                      <tr key={`${r.time}-${i}`} className="woodoo-tr" style={i % 2 === 0 ? S.trEven : S.trOdd}>
                       <td style={S.td}>
  {r.time
    ? r.time.replace("T", " ").substring(0, 19)
    : "—"}
</td>
                        <td style={S.td}>{r.Operateur || "—"}</td>
                        <td style={{ ...S.td, ...S.tdStack }}>{r.MelangePN || "—"}</td>
                        <td style={S.td}>{r.Temperature != null ? `${r.Temperature} °C` : "—"}</td>
                        <td style={S.td}>{r.DureeTraitement != null ? `${r.DureeTraitement} min` : "—"}</td>
                        <td style={S.td}>{r.TotalPlis ?? "—"}</td>
                        <td style={S.td}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>
                            {(r.StackPNs || []).map(s => (
                              <span key={s} style={S.stackBadge}>{s}</span>
                            ))}
                          </div>
                        </td>
                        <td style={S.td}>
                          {r.stats?.gain_moyen != null
                            ? <span style={{ color: "#4ade80", fontWeight: 800 }}>{r.stats.gain_moyen.toFixed(2)} %</span>
                            : "—"}
                        </td>
                        <td style={S.td}>
                          <div style={S.actionBtns}>
                            <button className="woodoo-voir" style={S.voirBtn} onClick={() => setDetails(r)}>
                              Voir →
                            </button>
                            <button
                              className="woodoo-edit"
                              style={{ ...S.voirBtn, background: "#60a5fa", color: "#fff" }}
                              onClick={() => setPlisPopup(r)}
                            >
                              Plis 📋
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div style={S.pagination}>
                <button style={{ ...S.pageBtn, opacity: currentPage === 1 ? 0.3 : 1 }}
                  disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                  ‹ Précédent
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                    acc.push(p); return acc;
                  }, [])
                  .map((p, i) => p === "..."
                    ? <span key={`dots-${i}`} style={S.pageDots}>…</span>
                    : <button key={p} style={{ ...S.pageBtn, ...(p === currentPage ? S.pageBtnActive : {}) }}
                        onClick={() => setCurrentPage(p)}>{p}</button>
                  )}
                <button style={{ ...S.pageBtn, opacity: currentPage === totalPages ? 0.3 : 1 }}
                  disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                  Suivant ›
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ════════════════════════════════════════
            MODAL — Détail imprégnation
        ════════════════════════════════════════ */}
        {details && (
          <div style={S.backdrop} onClick={() => setDetails(null)}>
            <div style={S.modal} onClick={e => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <div style={S.modalTitle}>Détail Imprégnation</div>
                  <div style={S.modalSub}>
                    {details.time} · {details.Operateur} · {details.MelangePN}
                  </div>
                </div>
                <button style={S.modalClose} onClick={() => setDetails(null)}>✕</button>
              </div>

              {/* Infos générales */}
              <div style={S.sectionTitle}>Paramètres process</div>
              <div style={S.mesureGrid}>
                {[
                  ["🌡️ Température",  details.Temperature,     "°C"],
                  ["⏱️ Durée",        details.DureeTraitement, "min"],
                  ["📦 Nb plis",      details.TotalPlis,       null],
                ].map(([label, val, unit]) => (
                  <div key={label} style={S.mesureCard}>
                    <div style={S.mesureLabel}>{label}</div>
                    <div style={S.mesureVal}>
                      {val ?? "—"} {unit && <span style={S.mesureUnit}>{unit}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Stacks concernés */}
              <div style={S.sectionTitle}>Stacks imprégné(s)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                {(details.StackPNs || []).map(s => (
                  <span key={s} style={S.stackBadgeLg}>{s}</span>
                ))}
              </div>

              {/* Stats agrégées */}
              <div style={S.sectionTitle}>Statistiques gain massique</div>
              <div style={S.mesureGrid}>
                {[
                  ["📈 Gain moyen",  details.stats?.gain_moyen,  "%"],
                  ["⬇️ Gain min",    details.stats?.gain_min,    "%"],
                  ["⬆️ Gain max",    details.stats?.gain_max,    "%"],
                  ["⚖️ Masse imprég. totale", details.stats?.masse_impreg_totale, "g"],
                  ["🪵 Masse cond. stack(s)", details.stats?.masse_cond_totale,   "g"],
                  ["✅ Plis avec gain",        details.stats?.nb_plis_avec_gain,  null],
                ].map(([label, val, unit]) => (
                  <div key={label} style={S.mesureCard}>
                    <div style={S.mesureLabel}>{label}</div>
                    <div style={{ ...S.mesureVal, fontSize: 18 }}>
                      {val != null ? (typeof val === "number" ? (unit === "%" ? val.toFixed(2) : val.toFixed(3)) : val) : "—"}
                      {unit && <span style={S.mesureUnit}> {unit}</span>}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <div style={{ ...S.sectionTitle, marginTop: 16, marginBottom: 0 }}>
                  Humidité après imprégnation · plis ({details.Rows?.length ?? 0})
                </div>
  <div style={{ display:"flex", gap:8 }}>
    {editMode ? (
      <>
        <button
          style={{ ...S.voirBtn, background:"#4ade80", color:"#000", opacity: saving ? 0.6 : 1 }}
          onClick={handleSaveMasses}
          disabled={saving}
        >
          {saving ? "Enregistrement…" : "✓ Enregistrer"}
        </button>
        <button
          style={{ ...S.voirBtn, background:"rgba(255,255,255,0.1)", color:"#cbd5e1" }}
          onClick={() => { setEditMode(false); setEditMasses({}); }}
          disabled={saving}
        >
          {ui("Annuler")}
        </button>
      </>
    ) : (
      <button
        className="woodoo-edit"
        style={{ ...S.voirBtn, background:"#60a5fa", color:"#fff" }}
        onClick={() => openEdit(details)}
      >
        ✏️ Modifier masses
      </button>
    )}
  </div>
</div>

              <HumiditePlisTables
                session={details}
                editMode={editMode}
                editMasses={editMasses}
                setEditMasses={setEditMasses}
              />

              <button className="woodoo-btn-lime" style={S.closeBtn} onClick={() => setDetails(null)}>
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════
            MODAL — Plis rapide (depuis bouton "Plis 📋")
        ════════════════════════════════════════ */}
        {plisPopup && (
          <div style={S.backdrop} onClick={() => setPlisPopup(null)}>
            <div style={{ ...S.modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <div style={S.modalTitle}>Plis — {(plisPopup.StackPNs || []).join(", ")}</div>
                  <div style={S.modalSub}>{plisPopup.time} · {plisPopup.MelangePN}</div>
                </div>
                <button style={S.modalClose} onClick={() => setPlisPopup(null)}>✕</button>
              </div>

              <HumiditePlisTables session={plisPopup} showMasses />

              <button className="woodoo-btn-lime" style={S.closeBtn} onClick={() => setPlisPopup(null)}>
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES — identiques au reste de l'app
// ─────────────────────────────────────────────────────────────────────────────
const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; }
  body { font-family: 'Inter', sans-serif; }
  .woodoo-input { background:#0d2233!important;color:#e2e8f0!important;border:1px solid #1e3a50!important;transition:border-color .2s,box-shadow .2s!important;outline:none!important; }
  .woodoo-input:focus { border-color:#E6FF3E!important;box-shadow:0 0 0 3px rgba(230,255,62,.15)!important; }
  .woodoo-input option { background:#0d2233; }
  .woodoo-th:hover { background:#1e3a50!important;color:#E6FF3E!important; }
  .woodoo-tr { transition:background .15s!important; }
  .woodoo-tr:hover td { background:#1a3248!important; }
  .woodoo-voir,.woodoo-edit { transition:background .2s,transform .15s,box-shadow .2s!important; }
  .woodoo-voir:hover { background:#fff!important;color:#000!important;box-shadow:0 4px 12px rgba(230,255,62,.3)!important;transform:translateY(-1px)!important; }
  .woodoo-edit:hover { background:#93c5fd!important;box-shadow:0 4px 12px rgba(96,165,250,.3)!important;transform:translateY(-1px)!important; }
  .woodoo-btn-lime { transition:background .2s,transform .15s,box-shadow .2s!important; }
  .woodoo-btn-lime:hover { background:#fff!important;transform:translateY(-1px)!important;box-shadow:0 4px 14px rgba(230,255,62,.35)!important; }
  .woodoo-btn-reset { transition:background .2s,transform .15s!important; }
  .woodoo-btn-reset:hover { background:#dc2626!important;transform:translateY(-1px)!important; }
  .woodoo-scroll::-webkit-scrollbar { height:6px; }
  .woodoo-scroll::-webkit-scrollbar-track { background:#0d1b2a; }
  .woodoo-scroll::-webkit-scrollbar-thumb { background:#E6FF3E;border-radius:3px; }
  @keyframes spin { to { transform:rotate(360deg); } }
  .woodoo-spin { display:inline-block;animation:spin 1s linear infinite; }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)} }
  .woodoo-tr { animation:fadeIn .2s ease both; }
`;

const S = {
  page:        { background:"linear-gradient(160deg,#041e2e 0%,#063549 60%,#052a3e 100%)",minHeight:"100vh",color:"#e2e8f0",fontFamily:"'Inter',sans-serif" },
  wrap:        { padding:"24px 28px",maxWidth:1600,margin:"0 auto" },
  header:      { display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28,flexWrap:"wrap",gap:16 },
  headerTitle: { fontSize:28,fontWeight:900,background:"linear-gradient(90deg,#fff 0%,#E6FF3E 100%)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" },
  headerSub:   { fontSize:13,color:"#94a3b8",marginTop:4 },
  statsRow:    { display:"flex",gap:12 },
  statCard:    { background:"rgba(255,255,255,0.04)",border:"1px solid #1e3a50",borderRadius:12,padding:"12px 20px",textAlign:"center",backdropFilter:"blur(8px)" },
  statNum:     { fontSize:26,fontWeight:900,color:"#fff" },
  statLabel:   { fontSize:11,color:"#94a3b8",marginTop:2,textTransform:"uppercase",letterSpacing:"0.08em" },
  panel:       { background:"rgba(255,255,255,0.03)",borderRadius:16,border:"1px solid #1e3a50",padding:24,backdropFilter:"blur(12px)" },
  filterBar:   { display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center" },
  filterWrap:  { flex:1,minWidth:160,position:"relative",display:"flex",alignItems:"center" },
  filterIcon:  { position:"absolute",left:10,fontSize:14,zIndex:1,pointerEvents:"none" },
  filterInput: { width:"100%",padding:"9px 12px 9px 32px",borderRadius:8,fontSize:13,fontWeight:500 },
  exportBtn:   { padding:"9px 18px",borderRadius:8,fontWeight:700,background:"#E6FF3E",border:"none",cursor:"pointer",fontSize:13,color:"#000",whiteSpace:"nowrap" },
  resetBtn:    { padding:"9px 18px",borderRadius:8,fontWeight:700,background:"#ef4444",color:"#fff",border:"none",cursor:"pointer",fontSize:13,whiteSpace:"nowrap" },
  resultCount: { display:"flex",alignItems:"center",gap:8,marginBottom:14,fontSize:13,fontWeight:600,color:"#E6FF3E" },
  resultDot:   { width:8,height:8,borderRadius:"50%",background:"#E6FF3E",boxShadow:"0 0 6px #E6FF3E" },
  loading:     { textAlign:"center",padding:40,fontSize:16,color:"#94a3b8" },
  errMsg:      { textAlign:"center",padding:20,color:"#f87171",fontWeight:600 },
  tableWrap:   { overflowX:"auto",borderRadius:12,border:"1px solid #1e3a50" },
  table:       { width:"100%",borderCollapse:"collapse",fontSize:13 },
  th:          { padding:"13px 14px",background:"#071e2f",color:"#94a3b8",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:"0.07em",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap",borderBottom:"1px solid #1e3a50" },
  td:          { padding:"11px 14px",borderBottom:"1px solid #0f2a3d",color:"#cbd5e1",textAlign:"center",whiteSpace:"nowrap",verticalAlign:"middle" },
  tdIndex:     { padding:"11px 14px",borderBottom:"1px solid #0f2a3d",color:"#64748b",fontSize:11,fontWeight:600,textAlign:"center" },
  tdStack:     { color:"#E6FF3E",fontWeight:700,fontFamily:"monospace",fontSize:12 },
  trEven:      { background:"transparent" },
  trOdd:       { background:"rgba(255,255,255,0.015)" },
  sortIcon:    { marginLeft:4,fontSize:10,color:"#64748b" },
  actionBtns:  { display:"flex",flexDirection:"column",gap:6,alignItems:"center" },
  voirBtn:     { padding:"6px 14px",borderRadius:6,cursor:"pointer",background:"#E6FF3E",fontWeight:700,border:"none",fontSize:12,color:"#000",whiteSpace:"nowrap" },
  stackBadge:  { background:"rgba(230,255,62,0.1)",border:"1px solid rgba(230,255,62,0.3)",borderRadius:999,padding:"3px 10px",fontSize:11,fontWeight:700,color:"#E6FF3E",fontFamily:"monospace" },
  stackBadgeLg:{ background:"rgba(230,255,62,0.1)",border:"1px solid rgba(230,255,62,0.4)",borderRadius:10,padding:"8px 14px",fontSize:13,fontWeight:700,color:"#E6FF3E",fontFamily:"monospace" },
  pagination:  { display:"flex",justifyContent:"center",alignItems:"center",gap:6,marginTop:18,flexWrap:"wrap" },
  pageBtn:     { padding:"7px 13px",borderRadius:8,border:"1px solid #1e3a50",background:"rgba(255,255,255,0.04)",color:"#cbd5e1",cursor:"pointer",fontWeight:700,fontSize:13 },
  pageBtnActive:{ background:"#E6FF3E",color:"#000",border:"1px solid #E6FF3E" },
  pageDots:    { color:"#64748b",fontSize:14,padding:"0 4px" },
  backdrop:    { position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(4px)",display:"flex",justifyContent:"center",alignItems:"center",padding:20,zIndex:200 },
  modal:       { background:"linear-gradient(160deg,#071e2f 0%,#0a2a40 100%)",color:"#e2e8f0",borderRadius:20,padding:28,width:"95%",maxWidth:900,maxHeight:"90vh",overflowY:"auto",border:"1px solid #1e3a50",boxShadow:"0 25px 60px rgba(0,0,0,0.6)" },
  modalHeader: { display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12 },
  modalTitle:  { fontSize:22,fontWeight:900,background:"linear-gradient(90deg,#fff,#E6FF3E)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" },
  modalSub:    { fontSize:12,color:"#64748b",marginTop:4 },
  modalClose:  { background:"rgba(255,255,255,0.08)",border:"1px solid #1e3a50",borderRadius:8,color:"#94a3b8",cursor:"pointer",fontSize:16,padding:"4px 10px" },
  sectionTitle:{ fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",color:"#64748b",borderBottom:"1px solid #1e3a50",paddingBottom:8,marginBottom:14,marginTop:24 },
  mesureGrid:  { display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10,marginBottom:8 },
  mesureCard:  { background:"rgba(255,255,255,0.03)",borderRadius:10,border:"1px solid #1e3a50",padding:"14px 16px" },
  mesureLabel: { fontSize:11,color:"#94a3b8",marginBottom:6,fontWeight:600 },
  mesureVal:   { fontSize:22,fontWeight:800,color:"#fff" },
  mesureUnit:  { fontSize:12,color:"#64748b",fontWeight:400 },
  closeBtn:    { width:"100%",marginTop:24,padding:"14px",borderRadius:10,fontWeight:900,fontSize:14,background:"#E6FF3E",cursor:"pointer",border:"none",color:"#000",letterSpacing:"0.05em" },
  humStackBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginBottom: 10,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid rgba(114,183,178,0.35)",
    background: "rgba(114,183,178,0.08)",
  },
  humStackLabel: { fontSize: 12, color: "#94a3b8", fontWeight: 600, flex: 1 },
  humStackVal: {
    fontSize: 18,
    fontWeight: 900,
    color: "#72b7b2",
    fontFamily: "monospace",
  },
};